// Components==============
import Button from 'components/UI/Button/Button';
import Slider from 'components/UI/Slider/Slider';
import Spark from 'components/UI/Spark/Spark';
import Toggle from 'components/UI/Toggle/Toggle';
import { AnimatePresence, motion } from 'framer-motion';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { framerFade } from 'utilities/FramerAnimations';
import NoActivity from '../../../NoActivity/NoActivity';
import { ReflectionItemContext } from '../../ReflectionItem';
import ReflectionTextarea from '../../ReflectionTextarea/ReflectionTextarea';
import EmojiSelector from '../EmojiSelector/EmojiSelector';
import './PeerReflection.module.scss';
// =========================

export default function PeerReflection() {
  const { peerId, currentStudent } = useContext(ReflectContext);
  const { reflection, save, saveState, reflectingOn } = useContext(
    ReflectionItemContext
  );

  const history = useHistory();
  const translate = useTranslate();
  const isTeacher = usePermission(['Docent', 'super-admin']);

  const [value, setValue] = useState(reflection?.note?.[0]?.answer || '');

  useEffect(() => setValue(reflection?.note?.[0]?.answer || ''), [
    reflection?.note?.[0]?.answer,
  ]);

  const userId = useRedux((state) => state.authentication?.user?.id);

  const note: ReflectionNoteEntity[] = [
    {
      question: {
        nl: 'Feedback',
        en: 'Feedback',
      },
      description: {
        nl: 'Hoe vind je dat deze leerling deze activiteit heeft uitgevoerd?',
        en: 'How do you think this student has performed on the activity?',
      },
      placeholder: {
        nl: 'Feedback resultaat',
        en: 'Feedback result',
      },
      answer: value,
      created_by: userId || 0,
    },
  ];

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    save({
      ...saveState,
      note,
      finished: true,
      reflection_on_student: currentStudent?.userId,
    });

    setValue((prev) => prev.trim());
    if (peerId)
      history.push(
        `/${isTeacher ? 'groei/feedback-geven' : 'groei/mijn-reflecties'}`
      );
  }

  if (!reflectingOn || !reflectingOn.contributed)
    return (
      <NoActivity
        notContributed={!!(reflectingOn && !reflectingOn?.contributed)}
      />
    );

  return (
    <motion.div styleName="wrapper" {...framerFade}>
      <div styleName="reflection">
        <div>
          <h3>{translate('general.terms.executed')}</h3>
          <span>{translate('reflection.show-muscles')}</span>
          <Toggle isToggled={!!reflectingOn?.contributed} />
        </div>
        {!!reflectingOn?.emotions.length && (
          <div>
            <h3>{translate('reflection.terms.experience')}</h3>
            <EmojiSelector staticContent={reflectingOn.emotions} />
          </div>
        )}
        {!!reflectingOn?.result && (
          <div>
            <h3>{translate('general.terms.result')}</h3>
            <span>{translate('reflection.reflect-on-activity')}</span>
            {!!reflectingOn?.result && <Slider value={reflectingOn?.result} />}
          </div>
        )}
        {!!reflectingOn?.sparks.length && (
          <div styleName="sparks">
            <div>
              <strong>{translate('general.terms.skills')}</strong>
              <span>{translate('reflection.show-activities')}</span>
            </div>
            <div styleName="sparks-wrapper ">
              <AnimatePresence>
                {reflectingOn?.sparks.map(
                  (spark) => !!spark && <Spark spark={spark} key={spark?.id} />
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
        <div />
      </div>
      <div styleName="peer-reflection">
        <form onSubmit={handleSubmit}>
          <ReflectionTextarea
            item={note[0]}
            value={value}
            setValue={setValue}
          />
          <Button
            color="grey"
            inactive={!value || value === reflection?.note?.[0]?.answer}
          >
            {translate(
              `reflection.${
                peerId ? `actions.send-reflection` : 'terms.send-feedback'
              }`
            )}
          </Button>
        </form>
      </div>
    </motion.div>
  );
}
